import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal, SectionLabel } from "../Reveal";
import { CONTACT } from "../../data/content";
import { Mail, ArrowUpRight, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TYPES = ["sponsorship", "music / beats", "dj booking", "collaboration", "other"];

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", company: "", inquiry_type: "sponsorship", budget: "", message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      toast.success("Message sent. Ian will get back to you soon.");
      setForm({ name: "", email: "", company: "", inquiry_type: "sponsorship", budget: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Try emailing directly.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full bg-[#0F0F0F] border border-white/15 text-white p-4 focus:outline-none focus:border-[#BAF173] focus:bg-white/5 transition-all placeholder:text-white/30 text-sm";

  return (
    <section id="contact" className="relative py-24 sm:py-32 md:py-40 bg-noise border-t border-white/10" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12">
        <SectionLabel>Get in Touch</SectionLabel>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Reveal>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold uppercase tracking-tight text-white leading-[0.95]">
                Let's Build<br />Something Hybrid
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 text-white/60 leading-relaxed">Sponsorships, custom beats, DJ bookings or collaborations — drop a message or reach out directly.</p>
            </Reveal>

            <Reveal delay={0.15}>
              <div className="mt-9 space-y-3">
                {CONTACT.emails.map((em) => (
                  <a key={em.value} href={`mailto:${em.value}`} className="flex items-center gap-3 text-white/75 hover:text-[#BAF173] transition-colors" data-testid={`email-${em.label.toLowerCase()}`}>
                    <Mail size={18} />
                    <span className="text-sm"><span className="text-white/40 uppercase tracking-widest text-xs mr-2">{em.label}</span>{em.value}</span>
                  </a>
                ))}
              </div>
            </Reveal>

            <Reveal delay={0.2}>
              <div className="mt-8 flex flex-wrap gap-2">
                {CONTACT.links.map((l) => (
                  <a key={l.label} href={l.href} target="_blank" rel="noopener noreferrer" className="group inline-flex items-center gap-1.5 font-display text-xs uppercase tracking-widest border border-white/15 px-4 py-2.5 text-white/70 hover:border-[#BAF173] hover:text-white transition-colors" data-testid="contact-link">
                    {l.label} <ArrowUpRight size={13} className="opacity-50 group-hover:opacity-100" />
                  </a>
                ))}
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <form onSubmit={submit} className="border border-white/10 bg-[#0A0A0A] p-7 md:p-9 space-y-4" data-testid="inquiry-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <input data-testid="input-name" className={inputCls} placeholder="Your name *" value={form.name} onChange={set("name")} />
                  <input data-testid="input-email" type="email" className={inputCls} placeholder="Email *" value={form.email} onChange={set("email")} />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <input data-testid="input-company" className={inputCls} placeholder="Brand / Company" value={form.company} onChange={set("company")} />
                  <input data-testid="input-budget" className={inputCls} placeholder="Budget (optional)" value={form.budget} onChange={set("budget")} />
                </div>
                <select data-testid="input-type" className={inputCls} value={form.inquiry_type} onChange={set("inquiry_type")}>
                  {TYPES.map((t) => <option key={t} value={t} className="bg-black capitalize">{t}</option>)}
                </select>
                <textarea data-testid="input-message" rows={5} className={inputCls} placeholder="Tell me about your project or partnership *" value={form.message} onChange={set("message")} />
                <button data-testid="submit-inquiry-btn" type="submit" disabled={loading} className="w-full flex items-center justify-center gap-2 font-display text-sm font-semibold uppercase tracking-widest px-8 py-4 bg-[#BAF173] text-black hover:bg-[#a5db65] transition-colors disabled:opacity-50">
                  {loading ? "Sending..." : <>Send Inquiry <Send size={16} /></>}
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
